// app/[lang]/(dashboard)/company-certificates/_components/certificate-card.tsx
"use client";

import { useIsArabic } from "@/hooks/use-isArabic";
import { Certificate } from "@/lib/types";
import { CalendarDays, Download, Eye } from "lucide-react";
import Image from "next/image";

interface CertificateCardProps {
  certificate: Certificate;
  onViewAction: (certificate: Certificate) => void;
  onDownloadAction: (url: string) => void;
}

export default function CertificateCard({
  certificate,
  onViewAction,
  onDownloadAction,
}: CertificateCardProps) {
  const { isArabic } = useIsArabic(); 


  // Only use companyLogo — featuredImage is the certificate document scan, not the brand logo
  const logoUrl = certificate.certificatesOption?.companyLogo?.node?.mediaItemUrl ?? null;
    
  const downloadUrl =
    certificate.certificatesOption?.certificateFile?.node?.mediaItemUrl ||
    certificate.featuredImage?.node?.mediaItemUrl;

  const displayTitle = isArabic ? certificate.arabicTitle : certificate.title;

  const translations = {
    expires_on: ["Expires on:", "تنتهي في:"],
  };



  return (
    <div className="flex flex-col items-center gap-y-3">
      <div className="relative group w-36 h-36">
        <div className="w-full h-full p-2 md:p-4 bg-white rounded-full overflow-hidden border-2 border-gray-200 flex items-center justify-center md:shadow-lg transition-shadow duration-500 group-hover:shadow-xl">
          {logoUrl && (
            <Image
              src={logoUrl}
              alt={`Logo`}
              width={120}
              height={120}
              className="h-full max-h-[90px] max-w-[90px] md:max-h-[110px] md:max-w-[110px] w-auto"
              style={{ objectFit: "contain" }}
              unoptimized
              priority
            />
          )}
        </div>
        <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center gap-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
          {downloadUrl && (
            <button
              onClick={() => onDownloadAction(downloadUrl)}
              className="p-3 bg-white rounded-full text-gray-800 hover:bg-gray-200 hover:cursor-pointer"
              aria-label="Download"
            >
              <Download size={20} />
            </button>
          )}
          <button
            onClick={() => onViewAction(certificate)}
            className="p-3 bg-white rounded-full text-gray-800 hover:bg-gray-200 hover:cursor-pointer"
            aria-label="View Image"
          >
            <Eye size={20} />
          </button>
        </div>
      </div>
      <p className="text-center text-sm font-medium text-gray-700 dark:text-textDark">
        {displayTitle}
      </p>
      {certificate.certificatesOption?.expiryDate && (
        <div className="hidden md:flex items-center gap-1.5 text-xs text-gray-500 dark:text-subtitleDark">
          <CalendarDays className="w-4 h-4" />
          <span>
            {/* 1. Select the correct translated text */}
            {isArabic
              ? translations.expires_on[1]
              : translations.expires_on[0]}{" "}
            {/* 2. Format the date using the correct locale */}
            {new Date(
              certificate.certificatesOption.expiryDate
            ).toLocaleDateString(isArabic ? "ar-SA" : "en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </span>
        </div>
      )}
    </div>
  );
}
